/**
 * Business Time Utilities
 *
 * Helper functions for consistent timezone display using tenant timezone.
 * All functions use date-fns-tz for Vercel compatibility.
 */

import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** Default timezone if not set in tenant */
export const DEFAULT_BUSINESS_TIMEZONE = 'Europe/Berlin';

/**
 * Format a time in the business timezone
 * @param time - Date object or ISO string
 * @param timezone - IANA timezone identifier (e.g., 'Europe/Berlin')
 * @param formatString - date-fns format string (default: 'HH:mm')
 * @returns Formatted time string
 */
export const formatBusinessTime = (
  time: string | Date,
  timezone: string,
  formatString: string = 'HH:mm'
): string => {
  const date = toBusinessDate(time, timezone);

  if (!date || Number.isNaN(date.getTime())) {
    return typeof time === 'string' ? time : '';
  }

  return formatInTimeZone(date, timezone, formatString);
};

/**
 * Format a date in the business timezone
 * @param date - Date object or ISO string
 * @param timezone - IANA timezone identifier (e.g., 'Europe/Berlin')
 * @param formatString - date-fns format string (default: 'dd.MM.yyyy')
 * @returns Formatted date string
 */
export const formatBusinessDate = (
  date: string | Date,
  timezone: string,
  formatString: string = 'dd.MM.yyyy'
): string => {
  const dateObj = typeof date === 'string' ? toBusinessDate(date, timezone) : date;

  if (!dateObj || Number.isNaN(dateObj.getTime())) {
    return typeof date === 'string' ? date : '';
  }

  return formatInTimeZone(dateObj, timezone, formatString);
};

/**
 * Format a date and time in the business timezone
 * @param dateTime - Date object or ISO string
 * @param timezone - IANA timezone identifier (e.g., 'Europe/Berlin')
 * @param formatString - date-fns format string (default: 'dd.MM.yyyy HH:mm')
 * @returns Formatted date-time string
 */
export const formatBusinessDateTime = (
  dateTime: string | Date,
  timezone: string,
  formatString: string = 'dd.MM.yyyy HH:mm'
): string => {
  const dateObj =
    typeof dateTime === 'string' ? toBusinessDate(dateTime, timezone) : dateTime;

  if (!dateObj || Number.isNaN(dateObj.getTime())) {
    return typeof dateTime === 'string' ? dateTime : '';
  }

  return formatInTimeZone(dateObj, timezone, formatString);
};

/**
 * Get business timezone from tenant or default
 * @param tenantTimezone - Optional tenant timezone
 * @returns IANA timezone identifier
 */
export const getBusinessTimezone = (tenantTimezone?: string | null): string => {
  return tenantTimezone || DEFAULT_BUSINESS_TIMEZONE;
};

/**
 * Parse incoming time values into a Date object aligned with the provided timezone.
 * Supports ISO strings and HH:mm[:ss] time-only strings.
 */
const toBusinessDate = (value: string | Date, timezone: string): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const timeOnlyMatch = /^\d{2}:\d{2}(:\d{2})?$/.test(trimmed);
  if (timeOnlyMatch) {
    const normalized = trimmed.length === 5 ? `${trimmed}:00` : trimmed;
    return fromZonedTime(`1970-01-01T${normalized}`, timezone);
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    let normalized = trimmed.replace(' ', 'T');
    const hasTimeComponent = normalized.includes('T');
    const hasOffset = /[+-]\d{2}:\d{2}|Z$/.test(normalized);

    if (!hasTimeComponent) {
      normalized = `${normalized}T00:00:00`;
    }

    return hasOffset ? new Date(normalized) : fromZonedTime(normalized, timezone);
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed;
  }

  return null;
};
